import moment from 'moment'
import { divideInNIntegers } from '../../../../components/common/mathHelper'
import { getcolumnDefs, summaryColumnDefs, TRANSACTION_LABEL, TRANSACTION_CONFIG } from './constants'

const TYPE_ORDER = ['Booking', 'Delivery', 'Refund']

export const buildUWCCriteriaHeaders = () => [
    { field: 'fromDate', header: 'From Collection Date', textAlign: 'left' },
    { field: 'toDate', header: 'To Collection Date', textAlign: 'left' },
    { field: 'selBranch', header: 'Company Branch', textAlign: 'left' },
    { field: 'selUser', header: 'Company User', textAlign: 'left' },
    { field: 'transaction', header: 'Transaction Type', textAlign: 'left' },
    { field: 'paymentType', header: 'Payment Type', textAlign: 'left' },
    { field: 'paymentMode', header: 'Payment Mode', textAlign: 'left' },
    { field: 'reportView', header: 'Report View', textAlign: 'left' },
]

export const buildUWCCriteriaData = ({ selBranch, selUser, fromDate, toDate, transaction, paymentType, paymentMode, reportView }) => ({
    fromDate: moment(fromDate).format('DD-MM-YYYY'),
    toDate: moment(toDate).format('DD-MM-YYYY'),
    selBranch: selBranch?.BranchName || '',
    selUser: selUser?.length === 1 ? selUser[0].UserName : 'All',
    transaction: transaction?.Transactions || '',
    paymentType: paymentType?.name || '',
    paymentMode: paymentMode?.ModeName || '',
    reportView: reportView?.view || '',
    dataType: 'entry',
})

const pickHeaders = (mainHeader) => {
    const lengths = divideInNIntegers(mainHeader.length, 4)
    let index = 0
    const picked = []
    lengths.forEach((span) => { picked.push(mainHeader[index]?.field); index += span })
    return { lengths, picked }
}

const makeSubHeader = (mainHeader, text0, text1, text2, text3) => {
    const { lengths, picked } = pickHeaders(mainHeader)
    return {
        dataType: 'subHeader',
        totalSubKey: 4,
        colSpan: Object.fromEntries(picked.map((key, i) => [key, lengths[i] || 1])),
        align: { [picked[3]]: 'right' },
        [picked[0]]: text0,
        [picked[1]]: text1,
        [picked[2]]: text2,
        [picked[3]]: text3,
    }
}
const groupBy = (arr, key) =>
    arr.reduce((acc, item) => {
        const k = item[key]
        if (k == null) return acc
        if (!acc[k]) acc[k] = []
        acc[k].push(item)
        return acc
    }, {})

const zeroTotals = () => ({ Quantity: 0, LRAmount: 0, OtherCharges: 0, Amount: 0, DeliveryExpense: 0, TotalAmount: 0 })

const sumDetailRows = (rows) =>
    rows.reduce((acc, r) => {
        acc.Quantity += Number(r.Quantity || 0)
        acc.LRAmount += Number(r.LRAmount || 0)
        acc.OtherCharges += Number(r.OtherCharges || 0)
        acc.Amount += Number(r.Amount || 0)
        acc.DeliveryExpense += Number(r.DeliveryExpense || 0)
        acc.TotalAmount += Number(r.TotalAmount || 0)
        return acc
    }, zeroTotals())

export const transformDetailedData = (Table, userDetail, Table3, UserName, UseFormattedLRinReports, cargoSettingFilterData) => {
    if (!Table?.length) return []

    const mainHeader = getcolumnDefs(UseFormattedLRinReports)
    const colSpanCount = mainHeader.findIndex(h => h.field === 'Quantity')
    const groupedByPayment = groupBy(Table, 'PaymentMode')
    const result = []
    let grandTotal = zeroTotals()

    Object.entries(groupedByPayment).forEach(([pm, pmRows]) => {
        result.push({
            dataType: 'subHeader',
            colSpan: { [mainHeader[0].field]: mainHeader.length },
            align: { [mainHeader[0].field]: 'left' },
            [mainHeader[0].field]: `Payment Mode: ${pm}`,
        })

        const labeledRows = pmRows.map(r => ({
            ...r,
            TranType: { BKNG: 'Booking', DEL: 'Delivery', RFND: 'Refund' }[r.TranType] || r.TranType,
        }))
        const groupedByTran = groupBy(labeledRows, 'TranType')
        let pmTotal = zeroTotals()

        TYPE_ORDER.forEach((tranType) => {
            const rows = groupedByTran[tranType]
            if (!rows) return

            const config = TRANSACTION_CONFIG[tranType] || TRANSACTION_CONFIG['Booking']

            result.push({
                dataType: 'header',
                top: '60px',
                weight: 40,
                ...Object.fromEntries(mainHeader.map(h => [
                    h.field,
                    h.field === 'DeliveryExpense' ? config.expenseLabel
                        : h.field === 'LRAmount' ? config.freightLabel
                            : h.field === 'TransactionDate' ? `${tranType} Date`
                                : h.headerName
                ]))
            })

            result.push(makeSubHeader(
                mainHeader,
                `Transaction: ${tranType}`,
                `State: ${userDetail?.[0]?.Statename}`,
                `City: ${userDetail?.[0]?.Cityname}`,
                `Branch: ${userDetail?.[0]?.Branchname}`,
            ))

            rows.forEach((row, i) => result.push({ ...row, SN: i + 1, dataType: 'entry' }))

            const rowTotals = sumDetailRows(rows)
            result.push({
                dataType: 'total',
                colSpan: { [mainHeader[0].field]: colSpanCount },
                align: { [mainHeader[0].field]: 'right' },
                [mainHeader[0].field]: `Total Amount: ${tranType}`,
                Quantity: rowTotals.Quantity.toFixed(2),
                LRAmount: rowTotals.LRAmount.toFixed(2),
                OtherCharges: rowTotals.OtherCharges.toFixed(2),
                Amount: rowTotals.Amount.toFixed(2),
                DeliveryExpense: rowTotals.DeliveryExpense.toFixed(2),
                TotalAmount: rowTotals.TotalAmount.toFixed(2),
            })

            const sign = tranType === 'Refund' ? -1 : 1
            Object.keys(pmTotal).forEach(k => { pmTotal[k] += sign * rowTotals[k] })
        })

        result.push({
            dataType: 'total',
            colSpan: { [mainHeader[0].field]: colSpanCount },
            align: { [mainHeader[0].field]: 'right' },
            [mainHeader[0].field]: `Net ${pm} Collection: ${UserName}`,
            Quantity: pmTotal.Quantity.toFixed(2),
            LRAmount: pmTotal.LRAmount.toFixed(2),
            OtherCharges: pmTotal.OtherCharges.toFixed(2),
            Amount: pmTotal.Amount.toFixed(2),
            DeliveryExpense: pmTotal.DeliveryExpense.toFixed(2),
            TotalAmount: pmTotal.TotalAmount.toFixed(2),
        })

        Object.keys(grandTotal).forEach(k => { grandTotal[k] += pmTotal[k] })
    })

    const dailyExpense = Number(Table3?.[0]?.DailyExpense || 0)

    result.push(
        {
            dataType: 'total',
            colSpan: { [mainHeader[0].field]: colSpanCount },
            align: { [mainHeader[0].field]: 'right' },
            [mainHeader[0].field]: `Net All Payment Collection: ${UserName}`,
            Quantity: grandTotal.Quantity.toFixed(2),
            LRAmount: grandTotal.LRAmount.toFixed(2),
            OtherCharges: grandTotal.OtherCharges.toFixed(2),
            Amount: grandTotal.Amount.toFixed(2),
            DeliveryExpense: grandTotal.DeliveryExpense.toFixed(2),
            TotalAmount: grandTotal.TotalAmount.toFixed(2),
        },
        {
            dataType: 'total',
            colSpan: { [mainHeader[0].field]: colSpanCount },
            align: { [mainHeader[0].field]: 'right' },
            [mainHeader[0].field]: 'LESS : Daily Expense Amount',
            TotalAmount: dailyExpense.toFixed(2),
        },
        {
            dataType: 'total',
            colSpan: { [mainHeader[0].field]: colSpanCount },
            align: { [mainHeader[0].field]: 'right' },
            [mainHeader[0].field]: `Net All Payment Collection - Expenses: ${UserName}`,
            TotalAmount: (grandTotal.TotalAmount - Math.abs(dailyExpense)).toFixed(2),
        }
    )

    if (cargoSettingFilterData?.[0]?.IsConvCharge === 1) {
        const convCharge = Number(Table3?.[0]?.TotalConvCharge || 0)
        result.push({
            dataType: 'total',
            colSpan: { [mainHeader[0].field]: colSpanCount },
            align: { [mainHeader[0].field]: 'right' },
            [mainHeader[0].field]: `Total All Payment Collection + Convenience Fee : ${UserName}`,
            TotalAmount: (convCharge + grandTotal.TotalAmount).toFixed(2),
        })
    }

    result.unshift({
        dataType: 'header2',
        colSpan: { [mainHeader[0].field]: mainHeader.length },
        align: { [mainHeader[0].field]: 'left' },
        [mainHeader[0].field]: `Detailed View (${UserName})`,
    })

    return result
}

const zeroSummaryTotals = () => ({
    PaidAmt: 0, PaidManAmt: 0, TopayAmt: 0, TopayManAmt: 0,
    DDDVAmt: 0, OnAccAmt: 0, CODAmt: 0, SelfAmt: 0, FOCAmt: 0, TotalAmt: 0,
})

export const transformSummaryData = (Table1, userDetail, UserName) => {
    if (!Table1?.length) return []

    const mainHeader = summaryColumnDefs
    const groupedByPaymentMode = groupBy(
        Table1.map(r => ({
            ...r,
            Transaction: TRANSACTION_LABEL[r.TYPE] || r.TYPE,
            TotalAmt: r.PaidAmt + r.PaidManAmt + r.TopayAmt + r.TopayManAmt + r.DDDVAmt + r.OnAccAmt + r.CODAmt + r.SelfAmt + r.FOCAmt,
        })),
        'PaymentMode'
    )

    const result = []
    const grandNet = zeroSummaryTotals()

    Object.entries(groupedByPaymentMode).forEach(([pm, rows]) => {
        result.push(makeSubHeader(
            mainHeader,
            `Payment Mode: ${pm}`,
            `State: ${userDetail?.[0]?.Statename}`,
            `City Name: ${userDetail?.[0]?.Cityname}`,
            `Branch Name: ${userDetail?.[0]?.Branchname}`,
        ))

        result.push({ dataType: 'header', weight: 40, ...Object.fromEntries(mainHeader.map(h => [h.field, h.headerName])) })

        let pmNet = zeroSummaryTotals()
        const groupedByType = groupBy(rows, 'TYPE')

        TYPE_ORDER.forEach(type => {
            const tRows = groupedByType[type]
            if (!tRows) return

            const total = zeroSummaryTotals()

            tRows.forEach(r => {
                Object.keys(total).forEach(k => {
                    total[k] += Number(r[k] || 0)
                })
            })

            result.push({
                dataType: 'entry',
                Transaction: TRANSACTION_LABEL[type] || type,
                ...total,
                TotalAmt: total.TotalAmt.toFixed(2),
            })

            const sign = type === 'Refund' ? -1 : 1
            Object.keys(pmNet).forEach(k => {
                pmNet[k] += sign * total[k]
            })
        })

        result.push({
            dataType: 'total',
            align: { [mainHeader[0].field]: 'left' },
            Transaction: 'Net Collection (A+B-C)',
            ...pmNet,
            TotalAmt: pmNet.TotalAmt.toFixed(2),
        })

        Object.keys(grandNet).forEach(k => { grandNet[k] += pmNet[k] })
    })

    result.push({
        dataType: 'total',
        align: { [mainHeader[0].field]: 'left' },
        Transaction: 'All Total Collection (A+B-C)',
        ...grandNet,
        TotalAmt: grandNet.TotalAmt.toFixed(2),
    })

    result.unshift({
        dataType: 'header2',
        colSpan: { [mainHeader[0].field]: mainHeader.length },
        align: { [mainHeader[0].field]: 'left' },
        [mainHeader[0].field]: `Summary View (${UserName})`,
    })

    return result
}